import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select, StatusPill, Textarea } from "@/components/ui-kit";
import { formatINR, formatDate } from "@/lib/format";
import { Plus, Trash2, Edit3, Calendar } from "lucide-react";

const empty = {
  client_id: "", client_name: "", client_mobile: "", booking_type: "Local",
  pickup: "", drop: "", journey_date: "", journey_time: "",
  vehicle_type: "Sedan", vehicle_id: "", driver_id: "", fare: 0,
  status: "inquiry", notes: "",
};

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const params = filter ? { status: filter } : {};
    const [b, v, d, c] = await Promise.all([
      api.get("/owner/bookings", { params }),
      api.get("/owner/vehicles"),
      api.get("/owner/drivers"),
      api.get("/owner/clients"),
    ]);
    setItems(b.data); setVehicles(v.data); setDrivers(d.data); setClients(c.data);
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (e) => {
    e?.preventDefault();
    const data = { ...editing, fare: Number(editing.fare || 0) };
    if (!data.vehicle_id) data.vehicle_id = null;
    if (!data.driver_id) data.driver_id = null;
    if (!data.client_id) data.client_id = null;
    if (data.id) await api.patch(`/owner/bookings/${data.id}`, data);
    else await api.post("/owner/bookings", data);
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => {
    if (!confirm("Delete booking?")) return;
    await api.delete(`/owner/bookings/${id}`); load();
  };

  const onClientPick = (cid) => {
    const c = clients.find((x) => x.id === cid);
    setEditing({ ...editing, client_id: cid, client_name: c?.name || editing.client_name, client_mobile: c?.mobile || editing.client_mobile });
  };

  return (
    <div data-testid="bookings-page">
      <PageHeader title="Bookings" subtitle="Inquiries to completed trips — full lifecycle."
        actions={<Btn data-testid="booking-add" onClick={() => { setEditing({...empty}); setOpen(true); }}><Plus className="h-4 w-4" /> New Booking</Btn>}
      />
      <div className="mb-4">
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="!w-56" data-testid="booking-filter">
          <option value="">All statuses</option>
          {["inquiry","confirmed","allocated","running","completed","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead><tr><th>#</th><th>Client</th><th>Type</th><th>Pickup → Drop</th><th>Date</th><th>Fare</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-zinc-500"><Calendar className="h-8 w-8 mx-auto mb-2 text-zinc-300" /> No bookings.</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                <td className="font-mono text-xs">{b.booking_number}</td>
                <td className="font-medium text-asphalt">{b.client_name}<div className="text-xs text-zinc-500">{b.client_mobile}</div></td>
                <td>{b.booking_type}</td>
                <td>{b.pickup} → {b.drop || "—"}</td>
                <td>{formatDate(b.journey_date)} {b.journey_time || ""}</td>
                <td className="font-mono">{formatINR(b.fare)}</td>
                <td><StatusPill status={b.status} /></td>
                <td className="text-right">
                  <Btn variant="ghost" data-testid={`booking-edit-${b.id}`} onClick={() => { setEditing(b); setOpen(true); }}><Edit3 className="h-4 w-4" /></Btn>
                  <Btn variant="ghost" data-testid={`booking-delete-${b.id}`} onClick={() => del(b.id)}><Trash2 className="h-4 w-4 text-red-600" /></Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} size="lg" onClose={() => setOpen(false)} title={editing?.id ? "Edit Booking" : "New Booking"} testid="booking-modal"
        footer={<><Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="booking-save" onClick={submit}>Save</Btn></>}>
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Client (existing)"><Select value={editing.client_id || ""} onChange={(e) => onClientPick(e.target.value)} data-testid="book-client-select">
              <option value="">— New / Walk-in —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.client_type})</option>)}
            </Select></Field>
            <Field label="Client Name" required><Input value={editing.client_name} onChange={(e) => setEditing({...editing, client_name: e.target.value})} required data-testid="book-client-name" /></Field>
            <Field label="Client Mobile"><Input value={editing.client_mobile || ""} onChange={(e) => setEditing({...editing, client_mobile: e.target.value})} /></Field>
            <Field label="Booking Type" required>
              <Select value={editing.booking_type} onChange={(e) => setEditing({...editing, booking_type: e.target.value})}>
                {["Local","Airport","OneWay","RoundTrip","Outstation","Employee","School","Package"].map((t) => <option key={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Pickup" required><Input value={editing.pickup} onChange={(e) => setEditing({...editing, pickup: e.target.value})} required data-testid="book-pickup" /></Field>
            <Field label="Drop"><Input value={editing.drop || ""} onChange={(e) => setEditing({...editing, drop: e.target.value})} data-testid="book-drop" /></Field>
            <Field label="Journey Date" required><Input type="date" value={editing.journey_date?.slice(0,10) || ""} onChange={(e) => setEditing({...editing, journey_date: e.target.value})} required data-testid="book-date" /></Field>
            <Field label="Journey Time"><Input type="time" value={editing.journey_time || ""} onChange={(e) => setEditing({...editing, journey_time: e.target.value})} /></Field>
            <Field label="Vehicle Type"><Select value={editing.vehicle_type} onChange={(e) => setEditing({...editing, vehicle_type: e.target.value})}>
              {["Sedan","SUV","Hatchback","Tempo Traveller","Bus","Luxury","EV"].map((t) => <option key={t}>{t}</option>)}
            </Select></Field>
            <Field label="Assigned Vehicle"><Select value={editing.vehicle_id || ""} onChange={(e) => setEditing({...editing, vehicle_id: e.target.value})}>
              <option value="">—</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration_number} · {v.vehicle_type}</option>)}
            </Select></Field>
            <Field label="Assigned Driver"><Select value={editing.driver_id || ""} onChange={(e) => setEditing({...editing, driver_id: e.target.value})}>
              <option value="">—</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.mobile}</option>)}
            </Select></Field>
            <Field label="Fare (INR)"><Input type="number" value={editing.fare} onChange={(e) => setEditing({...editing, fare: e.target.value})} data-testid="book-fare" /></Field>
            <Field label="Status">
              <Select value={editing.status} onChange={(e) => setEditing({...editing, status: e.target.value})}>
                {["inquiry","confirmed","allocated","running","completed","cancelled"].map((s) => <option key={s}>{s}</option>)}
              </Select>
            </Field>
            <div className="col-span-2"><Field label="Notes"><Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({...editing, notes: e.target.value})} /></Field></div>
          </form>
        )}
      </Modal>
    </div>
  );
}
